/**
 * RT ポップアップの「バックエンドデータ完全依存」移行用の純粋ロジック。
 *
 * - 表示情報（ロゴ/規約URL/送客キー等）は request_data の各行 detail_info から組み立てる。
 * - 表示判定は各行の *_arr（null許容）と age_range_mr(int4multirange) で行う。
 * - 副作用（ダイアログ emit）は呼び出し側に委ねるため、ここには含めない。
 */

export interface RtDetailInfo {
  content?: string | null
  client_service_name?: string | null
  client_name?: string | null
  terms_url?: string | null
  privacy_url?: string | null
  dialog_type?: string | null
  display_order_num?: number | null
  // old_permission_type は DB 制約で実在 permission のみが入る。null/未設定なら NewOnly。
  old_permission_type?: number | null
  // API レスポンスではオブジェクト or JSON 文字列 ('{"ApiEndpointKey":"sendJobs919"}') の両方が来る。
  additional_dialog_info?: Record<string, unknown> | string | null
}

export interface RtConditionRow {
  client_id?: string
  permission_type?: number | string
  dialog_name?: string
  request_able?: boolean
  display_order_num?: number | null
  age_range_mr?: string | null
  template_ids_arr?: string[] | null
  prefectures_arr?: string[] | null
  keyword_arr?: string[] | null
  exclude_keyword_arr?: string[] | null
  grade_year_arr?: Array<string | number> | null
  detail_info?: RtDetailInfo | null
  google_conversation_id?: number | null
  google_conversation_label?: string | null
  [key: string]: unknown
}

export interface RtConditionContext {
  formatId: string
  formatData: unknown
  basicData: { age?: number | string; [key: string]: unknown }
  addressCheck: (basicData: unknown, prefectures: string[]) => boolean
  keywordCheckRirekisho: (formatData: unknown, keywords: string[]) => boolean
  keywordCheckShokumukeirekisho: (formatData: unknown, keywords: string[]) => boolean
  // additional_dialog_info.LicenseKeywords 用（資格欄のみのキーワード一致）。任意。
  keywordCheckOnlyLicense?: (formatId: string, formatData: unknown, keyword: string) => boolean
}

export interface RtDialogInfo {
  clientId: string
  dialogName: string
  clientServiceName: string
  clientName: string
  logoImage: string
  termurl: string
  privacyurl: string
  sendApiEndpointKey?: string
  [key: string]: unknown
}

/** detail_info が空（{}）= API が非表示(display_flag !== '1')で返した行。表示不可。 */
export function hasDisplayableDetailInfo (row: RtConditionRow): boolean {
  const di = row.detail_info
  return !!di && typeof di === 'object' && Object.keys(di).length > 0
}

/** logoImage(content) がファイル名を持つ有効なパスか（'/static/.../.png' のような空名は無効）。 */
export function hasValidLogoImage (logoImage: string | null | undefined): boolean {
  if (!logoImage || typeof logoImage !== 'string') return false
  const fileName = logoImage.split('/').pop() || ''
  // 拡張子の前にファイル名本体があること（'.png' だけは無効）。
  return /[^/.][^/]*\.[A-Za-z0-9]+$/.test(fileName)
}

/** auto_rt を除いた RT ダイアログ行のコピー（バックエンド配列順を保持）。 */
export function filterRtDialogRows (rows: unknown): RtConditionRow[] {
  const list = Array.isArray(rows)
    ? rows
    : (rows && typeof rows === 'object' ? Object.values(rows as Record<string, unknown>) : [])
  return list.filter((row): row is RtConditionRow => {
    if (!row || typeof row !== 'object') return false
    const name = (row as RtConditionRow).dialog_name
    return !!name && name !== 'auto_rt'
  })
}

/**
 * PostgreSQL int4multirange を解釈し age が含まれるかを返す。
 * 例: "{[23,60)}" / "{[23,40),[50,60)}"。`[`/`]` は端含む、`(`/`)` は端含まない。
 * null/空は「制約なし」= true。
 */
export function isAgeInMultirange (
  multirange: string | null | undefined,
  age: number | string | null | undefined
): boolean {
  if (multirange == null || String(multirange).trim() === '') return true
  const n = Number(age)
  if (!Number.isFinite(n)) return false
  const body = String(multirange).trim().replace(/^\{/, '').replace(/\}$/, '')
  if (body.trim() === '') return true
  const ranges = body.match(/[[(]\s*-?\d*\s*,\s*-?\d*\s*[\])]/g) || []
  return ranges.some((r) => {
    const lowerInc = r[0] === '['
    const upperInc = r[r.length - 1] === ']'
    const parts = r.slice(1, -1).split(',')
    const loStr = (parts[0] ?? '').trim()
    const hiStr = (parts[1] ?? '').trim()
    const lo = loStr === '' ? -Infinity : Number(loStr)
    const hi = hiStr === '' ? Infinity : Number(hiStr)
    return (lowerInc ? n >= lo : n > lo) && (upperInc ? n <= hi : n < hi)
  })
}

/**
 * 行の条件配列で表示可否を判定する。各条件は任意で、null/空配列は「制約なし(通過)」。
 * - template_ids_arr: 対象 formatId を含むこと
 * - prefectures_arr: addressCheck
 * - keyword_arr: 履歴書/職務経歴書いずれかでヒット
 * - exclude_keyword_arr: ヒットしたら除外
 * - age_range_mr: int4multirange で年齢が含まれること
 * - grade_year_arr: Number(formatData.gradeYear) が含まれること
 */
export function matchesRtRowCondition (row: RtConditionRow, ctx: RtConditionContext): boolean {
  const add = parseAdditionalDialogInfo((row.detail_info || {}).additional_dialog_info)

  // forceDisplayFormat: 対象 formatId なら他条件を無視して強制表示（レガシーの
  // `if (formatId === '...') { isXxx = true }` 相当）。
  if (hasForceDisplayFormat(row, ctx.formatId)) return true

  const tids = row.template_ids_arr
  if (Array.isArray(tids) && tids.length > 0 && (!ctx.formatId || !tids.includes(ctx.formatId))) {
    return false
  }

  const prefs = row.prefectures_arr
  if (Array.isArray(prefs) && prefs.length > 0 && !ctx.addressCheck(ctx.basicData, prefs)) {
    return false
  }

  const kws = row.keyword_arr
  if (Array.isArray(kws) && kws.length > 0) {
    let hit = ctx.keywordCheckRirekisho(ctx.formatData, kws) ||
      ctx.keywordCheckShokumukeirekisho(ctx.formatData, kws)
    // LicenseKeywords: 資格欄のみのキーワード一致を OR で許可（firstconnect 等）。
    if (!hit) hit = matchesLicenseKeywords(add, ctx)
    if (!hit) return false
  }

  const ngs = row.exclude_keyword_arr
  if (Array.isArray(ngs) && ngs.length > 0) {
    const hit = ctx.keywordCheckRirekisho(ctx.formatData, ngs) ||
      ctx.keywordCheckShokumukeirekisho(ctx.formatData, ngs)
    if (hit) return false
  }

  const grades = row.grade_year_arr
  if (Array.isArray(grades) && grades.length > 0) {
    const fd = ctx.formatData as { gradeYear?: unknown } | null | undefined
    const gradeYear = Number(fd ? fd.gradeYear : NaN)
    if (!Number.isFinite(gradeYear) || !grades.map(Number).includes(gradeYear)) {
      return false
    }
  }

  if (!isAgeInMultirange(row.age_range_mr, ctx.basicData.age)) return false

  // additional_dialog_info の入力検証ゲート（MobileValid など）。
  if (!matchesRtDisplayGate(add, ctx)) return false
  return true
}

/** additional_dialog_info.LicenseKeywords のいずれかが資格欄に一致するか。 */
function matchesLicenseKeywords (add: Record<string, unknown>, ctx: RtConditionContext): boolean {
  const list = add.LicenseKeywords
  if (!Array.isArray(list) || list.length === 0 || !ctx.keywordCheckOnlyLicense) return false
  return list.some((kw) =>
    typeof kw === 'string' && ctx.keywordCheckOnlyLicense!(ctx.formatId, ctx.formatData, kw))
}

/** 携帯電話番号として妥当か（先頭 070/080/090・全桁数字）。 */
function isValidMobileTel (bd: Record<string, unknown>): boolean {
  const t1 = String(bd.tel1 ?? '').replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xFEE0))
  const t2 = String(bd.tel2 ?? '')
  const t3 = String(bd.tel3 ?? '')
  const all = `${t1}${t2}${t3}`
  if (!(bd.tel1 && bd.tel2 && bd.tel3) || !/^[0-9０-９]+$/.test(`${bd.tel1}${t2}${t3}`)) return false
  return /^[0-9]+$/.test(all) && ['070', '080', '090'].includes(t1)
}

/**
 * 表示ダイアログ用の additional_dialog_info 入力検証ゲート。フラグ未設定は通過。
 * - MobileValid: 携帯番号(070/080/090)が妥当
 * - NameValid / TelValid / MailValid / BirthYearValid / DomesticStudentValid:
 *   auto_rt ゲート(matchesAutoRtExtraGate)と同義
 * - GenderValid: basicData.sex が入力済み
 * - BirthdayValid: basicData.birthYear / birthMonth / birthDay がすべて入力済み
 */
export function matchesRtDisplayGate (add: Record<string, unknown>, ctx: RtConditionContext): boolean {
  const bd = (ctx.basicData || {}) as Record<string, unknown>
  const fd = (ctx.formatData || {}) as Record<string, unknown>

  if (add.MobileValid === true && !isValidMobileTel(bd)) return false
  if (add.NameValid === true && !(bd.lastName && bd.firstName)) return false
  if (add.TelValid === true) {
    const tel = `${String(bd.tel1 ?? '')}${String(bd.tel2 ?? '')}${String(bd.tel3 ?? '')}`
    if (!(bd.tel1 && bd.tel2 && bd.tel3 && /^[0-9０-９]+$/.test(tel))) return false
  }
  if (add.MailValid === true && !bd.email) return false
  if (add.BirthYearValid === true && !/^[0-9]{4}$/.test(String(bd.birthYear ?? ''))) return false
  if (add.GenderValid === true && !bd.sex) return false
  if (add.BirthdayValid === true && !(bd.birthYear && bd.birthMonth && bd.birthDay)) return false
  if (add.DomesticStudentValid === true && fd.foreignStudent) return false
  return true
}

/** additional_dialog_info（オブジェクト or JSON 文字列）をオブジェクトに正規化する。 */
export function parseAdditionalDialogInfo (value: unknown): Record<string, unknown> {
  if (!value) return {}
  if (typeof value === 'object') return value as Record<string, unknown>
  if (typeof value === 'string' && value.trim() !== '') {
    try {
      const parsed: unknown = JSON.parse(value)
      return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : {}
    } catch (_e) {
      return {}
    }
  }
  return {}
}

/** detail_info からダイアログ情報を完全バックエンド依存で組み立てる（null はそのまま空）。 */
export function buildRtDialogInfo (row: RtConditionRow): RtDialogInfo {
  const di: RtDetailInfo = row.detail_info || {}
  const add = parseAdditionalDialogInfo(di.additional_dialog_info)
  const info: RtDialogInfo = {
    ...add,
    clientId: String(row.client_id || ''),
    dialogName: String(row.dialog_name || ''),
    clientServiceName: di.client_service_name || '',
    // backend に企業名フィールドが無いため暫定で client_service_name を流用
    clientName: di.client_name || '',
    logoImage: di.content || '',
    termurl: di.terms_url || '',
    privacyurl: di.privacy_url || ''
  }
  // ApiEndpointKey が存在し非空のときだけ sendApiEndpointKey を持たせる（無い/null は付与しない）。
  const apiKey = add.ApiEndpointKey
  if (typeof apiKey === 'string' && apiKey.trim() !== '') {
    info.sendApiEndpointKey = apiKey
  }
  // デバッグ: リアクティブのゲッター越しではなくプリミティブ値を確認するためディープクローンで出力。
  try {
    console.log('[RT_MIGRATION] raw detail_info', JSON.parse(JSON.stringify(di)))
    console.log('[RT_MIGRATION] built dialogInfo', JSON.parse(JSON.stringify(info)))
  } catch (_e) {
    console.log('[RT_MIGRATION] built dialogInfo (raw)', info)
  }
  return info
}

/** detail_info.dialog_type を解決（無ければ共通ダイアログ）。 */
export function resolveRtDialogType (row: RtConditionRow): string {
  const di: RtDetailInfo = row.detail_info || {}
  return di.dialog_type || 'open-realtimetransfercommon-dialog'
}

/* ------------------------------------------------------------------ *
 * auto_rt（dialog_name === 'auto_rt'）専用の純データ駆動ロジック。
 * ------------------------------------------------------------------ */

export type AutoRtRequestableMode = 'NewOnly' | 'SetAutoRT'

export interface AutoRtRequestableSpec {
  mode: AutoRtRequestableMode
  newPermissionType: number
  oldPermissionType: number
}

/** 入力検証ゲート（additional_dialog_info のフラグ）で参照する値。 */
export interface AutoRtGateContext {
  basicData: Record<string, unknown>
  // shokumukeirekisho フォーマット時は呼び出し側で otherFormatData に差し替えた値を渡す。
  formatData: Record<string, unknown>
  // exclude_mail_domains 判定用（$firebase.user.email）。
  userEmail?: string | null
}

export interface AutoRtDialogInfo {
  logoImage: string
  clientId: string
  dialogName: string
  clientName: string
  termurl: string
  privacyurl: string
  occupationurl: string
  handlingurl: string
  clientServiceName: string
  privacyLabel: string
  showTrailingBr: boolean
  permissionType: number
  oldPermissionType: number
  googleConversationId: number | null
  googleConversationLabel: string | null
  [key: string]: unknown
}

/**
 * auto_rt 行のみを抽出し display_order_num 昇順で返す。
 * detail_info が空オブジェクト（{}）の行は API が display_flag !== '1'（非表示）で
 * 返したものなのでスキップする。同一 display_order_num は元配列順を維持。
 */
export function sortedAutoRtRows (rows: unknown): RtConditionRow[] {
  const list = Array.isArray(rows)
    ? rows
    : (rows && typeof rows === 'object' ? Object.values(rows as Record<string, unknown>) : [])
  const autoRows = list.filter((row): row is RtConditionRow => {
    if (!row || typeof row !== 'object') return false
    const r = row as RtConditionRow
    if (r.dialog_name !== 'auto_rt') return false
    const di = r.detail_info
    return !!di && typeof di === 'object' && Object.keys(di).length > 0
  })
  const orderOf = (r: RtConditionRow): number => {
    const di: RtDetailInfo = r.detail_info || {}
    const raw = r.display_order_num != null ? r.display_order_num : di.display_order_num
    const n = Number(raw)
    return Number.isFinite(n) ? n : 0
  }
  return autoRows
    .map((row, idx) => ({ row, idx }))
    .sort((a, b) => (orderOf(a.row) - orderOf(b.row)) || (a.idx - b.idx))
    .map((x) => x.row)
}

/** additional_dialog_info.forceDisplayFormat に formatId が含まれるか。 */
export function hasForceDisplayFormat (row: RtConditionRow, formatId: string): boolean {
  const add = parseAdditionalDialogInfo((row.detail_info || {}).additional_dialog_info)
  const list = add.forceDisplayFormat
  return Array.isArray(list) && list.includes(formatId)
}

/**
 * additional_dialog_info の入力検証ゲート。フラグ未設定の項目は通過。
 * - NameValid: lastName かつ firstName
 * - TelValid: tel1/tel2/tel3 全て存在 かつ 連結が /^[0-9０-９]+$/
 * - MailValid: basicData.email が truthy
 * - BirthYearValid: basicData.birthYear が /^[0-9]{4}$/
 * - DomesticStudentValid: !formatData.foreignStudent
 * - exclude_mail_domains: userEmail に部分一致したら除外
 */
export function matchesAutoRtExtraGate (row: RtConditionRow, ctx: AutoRtGateContext): boolean {
  const add = parseAdditionalDialogInfo((row.detail_info || {}).additional_dialog_info)
  const bd = ctx.basicData || {}
  const fd = ctx.formatData || {}

  if (add.NameValid === true) {
    if (!(bd.lastName && bd.firstName)) return false
  }
  if (add.MobileValid === true) {
    const t1 = String(bd.tel1 ?? '').replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xFEE0))
    const all = `${t1}${String(bd.tel2 ?? '')}${String(bd.tel3 ?? '')}`
    if (!(bd.tel1 && bd.tel2 && bd.tel3) || !/^[0-9]+$/.test(all) || !['070', '080', '090'].includes(t1)) {
      return false
    }
  }
  if (add.TelValid === true) {
    const tel = `${String(bd.tel1 ?? '')}${String(bd.tel2 ?? '')}${String(bd.tel3 ?? '')}`
    if (!(bd.tel1 && bd.tel2 && bd.tel3 && /^[0-9０-９]+$/.test(tel))) return false
  }
  if (add.MailValid === true) {
    if (!bd.email) return false
  }
  if (add.BirthYearValid === true) {
    if (!/^[0-9]{4}$/.test(String(bd.birthYear ?? ''))) return false
  }
  if (add.DomesticStudentValid === true) {
    if (fd.foreignStudent) return false
  }
  const domains = add.exclude_mail_domains
  if (Array.isArray(domains) && domains.length > 0) {
    const email = String(ctx.userEmail ?? '')
    if (domains.some((d) => typeof d === 'string' && d !== '' && email.includes(d))) {
      return false
    }
  }
  return true
}

/**
 * requestable 方式（2択）を解決する。
 * - NewOnly: old_permission_type が null/未設定、または NotSendOldPermission === true。
 * - SetAutoRT: old_permission_type 設定あり かつ NotSendOldPermission 無し。
 */
export function resolveRequestableMode (row: RtConditionRow): AutoRtRequestableSpec {
  const di: RtDetailInfo = row.detail_info || {}
  const add = parseAdditionalDialogInfo(di.additional_dialog_info)
  const newPermissionType = Number(row.permission_type) || 0
  const oldRaw = di.old_permission_type
  const isNewOnly = oldRaw == null || add.NotSendOldPermission === true
  if (isNewOnly) {
    return { mode: 'NewOnly', newPermissionType, oldPermissionType: 0 }
  }
  return { mode: 'SetAutoRT', newPermissionType, oldPermissionType: Number(oldRaw) || 0 }
}

/** detail_info + additional_dialog_info から autoRTInfo（表示情報）を組み立てる。 */
export function buildAutoRtDialogInfo (row: RtConditionRow): AutoRtDialogInfo {
  const di: RtDetailInfo = row.detail_info || {}
  const add = parseAdditionalDialogInfo(di.additional_dialog_info)
  const spec = resolveRequestableMode(row)
  return {
    logoImage: di.content || '',
    clientId: String(row.client_id || ''),
    dialogName: String(row.dialog_name || 'auto_rt'),
    clientName: di.client_name || '',
    termurl: di.terms_url || '',
    privacyurl: di.privacy_url || '',
    occupationurl: typeof add.occupationurl === 'string' ? add.occupationurl : '',
    handlingurl: typeof add.handlingurl === 'string' ? add.handlingurl : '',
    clientServiceName: di.client_service_name || '',
    privacyLabel: typeof add.privacyLabel === 'string' ? add.privacyLabel : '',
    showTrailingBr: add.showTrailingBr === true,
    permissionType: spec.newPermissionType,
    oldPermissionType: spec.oldPermissionType,
    googleConversationId: (row.google_conversation_id as number | null) ?? null,
    googleConversationLabel: (row.google_conversation_label as string | null) ?? null
  }
}
